import { useEffect, useRef, useState } from "react";
import { CloseCircle, Microphone, Pause, Play, Send2 } from "reicon-react";

const BAR_COUNT = 18;
const RECORD_BAR_COUNT = 28;
const MIN_VOICE_MS = 400;

type VisualizerState = "idle" | "listening" | "speaking";

export function EngageAudioPlayer({ url }: { url?: string }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [playing, setPlaying] = useState(false);
	const [elapsed, setElapsed] = useState(0);
	const [duration, setDuration] = useState(0);
	const live = useAnimatedBands(BAR_COUNT, playing);
	const snapshot = snapshotBands(url || "voice", BAR_COUNT);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		const onTime = () => setElapsed(audio.currentTime || 0);
		const onMeta = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
		const onEnded = () => {
			setPlaying(false);
			setElapsed(0);
		};
		audio.addEventListener("timeupdate", onTime);
		audio.addEventListener("loadedmetadata", onMeta);
		audio.addEventListener("durationchange", onMeta);
		audio.addEventListener("ended", onEnded);
		return () => {
			audio.removeEventListener("timeupdate", onTime);
			audio.removeEventListener("loadedmetadata", onMeta);
			audio.removeEventListener("durationchange", onMeta);
			audio.removeEventListener("ended", onEnded);
		};
	}, [url]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio || !url || !playing) return;
		void audio.play().catch(() => setPlaying(false));
		return () => {
			try {
				audio.pause();
			} catch {
				// jsdom has no media playback
			}
		};
	}, [playing, url]);

	return (
		<div className="engage-voice" aria-label="Voice message">
			{url ? <audio ref={audioRef} src={url} preload="metadata" hidden /> : null}
			<button
				className="engage-voice-play"
				type="button"
				disabled={!url}
				aria-label={playing ? "Pause voice message" : "Play voice message"}
				onClick={() => setPlaying((on) => !on)}
			>
				{playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
			</button>
			<BarVisualizer state={playing ? "speaking" : "idle"} bands={playing ? live : snapshot} />
			<span className="engage-voice-time">{formatClock(playing || elapsed > 0 ? elapsed : duration)}</span>
		</div>
	);
}

export function EngageVoiceMic({ disabled, onStart }: { disabled?: boolean; onStart: () => void }) {
	return (
		<button
			className="team-icon-btn engage-speech-mic"
			type="button"
			disabled={disabled}
			aria-label="Start recording"
			onClick={onStart}
		>
			<Microphone size={18} aria-hidden="true" />
		</button>
	);
}

export function EngageVoiceCapture({
	disabled,
	onCancel,
	onRecorded,
	onError,
}: {
	disabled?: boolean;
	onCancel: () => void;
	onRecorded: (file: File) => void;
	onError: (message: string) => void;
}) {
	const [state, setState] = useState<"recording" | "processing">("recording");
	const [elapsed, setElapsed] = useState(0);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const startedAt = useRef(0);
	const sendOnStop = useRef(false);
	const onCancelRef = useRef(onCancel);
	const onRecordedRef = useRef(onRecorded);
	const onErrorRef = useRef(onError);
	onCancelRef.current = onCancel;
	onRecordedRef.current = onRecorded;
	onErrorRef.current = onError;
	const live = useStreamBands(stream, RECORD_BAR_COUNT, state === "recording");
	const pulse = useAnimatedBands(RECORD_BAR_COUNT, state === "processing");
	const bands = state === "processing" ? pulse : live;

	useEffect(() => {
		let gone = false;
		let localStream: MediaStream | null = null;
		void (async () => {
			try {
				if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
					onErrorRef.current("This browser cannot record a voice message.");
					onCancelRef.current();
					return;
				}
				localStream = await navigator.mediaDevices.getUserMedia({
					audio: { echoCancellation: true, noiseSuppression: true },
				});
				if (gone) {
					stopTracks(localStream);
					return;
				}
				const mime = pickRecorderMime();
				const recorder = new MediaRecorder(localStream, mime ? { mimeType: mime } : undefined);
				chunksRef.current = [];
				recorder.ondataavailable = (event) => {
					if (event.data.size) chunksRef.current.push(event.data);
				};
				recorder.onstop = () => {
					const type = recorder.mimeType || mime || "audio/webm";
					const blob = new Blob(chunksRef.current, { type });
					const held = Date.now() - startedAt.current;
					stopTracks(localStream);
					setStream(null);
					if (!sendOnStop.current || held < MIN_VOICE_MS || blob.size < 32) {
						onCancelRef.current();
						return;
					}
					const ext = type.includes("ogg") ? "ogg" : "webm";
					onRecordedRef.current(new File([blob], `voice.${ext}`, { type }));
				};
				recorderRef.current = recorder;
				startedAt.current = Date.now();
				setStream(localStream);
				recorder.start();
			} catch (caught) {
				if (localStream) stopTracks(localStream);
				if (!gone) {
					onErrorRef.current(microphoneErrorMessage(caught));
					onCancelRef.current();
				}
			}
		})();
		return () => {
			gone = true;
			sendOnStop.current = false;
			const recorder = recorderRef.current;
			if (recorder && recorder.state !== "inactive") recorder.stop();
			else if (localStream) stopTracks(localStream);
			recorderRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (state !== "recording") return;
		const tick = window.setInterval(() => {
			if (startedAt.current) setElapsed(Date.now() - startedAt.current);
		}, 200);
		return () => window.clearInterval(tick);
	}, [state]);

	function finish(send: boolean) {
		if (state === "processing") return;
		sendOnStop.current = send;
		setState("processing");
		const recorder = recorderRef.current;
		if (recorder && recorder.state !== "inactive") recorder.stop();
		else onCancel();
	}

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") finish(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [state]);

	return (
		<div className="engage-speech is-recording">
			<button
				className="team-icon-btn"
				type="button"
				disabled={disabled || state === "processing"}
				aria-label="Cancel recording"
				onClick={() => finish(false)}
			>
				<CloseCircle size={18} aria-hidden="true" />
			</button>
			<button
				className="engage-voice-btn"
				type="button"
				disabled={disabled || state === "processing"}
				aria-label="Stop recording"
				onClick={() => finish(true)}
			>
				<BarVisualizer
					state={state === "processing" ? "listening" : "speaking"}
					bands={bands}
					barCount={RECORD_BAR_COUNT}
				/>
				<span className="engage-voice-time">{formatClock(elapsed / 1000)}</span>
			</button>
			<button
				className="team-send"
				type="button"
				disabled={disabled || state === "processing"}
				aria-label="Send voice message"
				onClick={() => finish(true)}
			>
				<Send2 size={18} aria-hidden="true" />
			</button>
		</div>
	);
}

function BarVisualizer({
	state,
	bands,
	barCount = BAR_COUNT,
}: {
	state: VisualizerState;
	bands: number[];
	barCount?: number;
}) {
	const values = bands.length === barCount ? bands : padBands(bands, barCount);
	return (
		<span className="engage-bars" data-state={state} aria-hidden="true">
			{values.map((volume, index) => (
				<i key={index} style={{ height: `${Math.min(100, Math.max(16, volume * 100))}%` }} />
			))}
		</span>
	);
}

function useAnimatedBands(barCount: number, active: boolean): number[] {
	const [bands, setBands] = useState(() => Array.from({ length: barCount }, () => 0.2));

	useEffect(() => {
		if (!active) {
			setBands(Array.from({ length: barCount }, () => 0.2));
			return;
		}
		let frame = 0;
		const draw = (stamp: number) => {
			setBands(demoBands(barCount, stamp / 1000));
			frame = requestAnimationFrame(draw);
		};
		frame = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(frame);
	}, [active, barCount]);

	return bands;
}

function useStreamBands(stream: MediaStream | null, barCount: number, active: boolean): number[] {
	const animated = useAnimatedBands(barCount, active && !stream);
	const [bands, setBands] = useState(() => Array.from({ length: barCount }, () => 0.2));

	useEffect(() => {
		if (!active || !stream) return;
		const analyser = attachStreamAnalyser(stream);
		if (!analyser) return;
		let frame = 0;
		const draw = () => {
			const bins = new Uint8Array(analyser.frequencyBinCount);
			analyser.getByteFrequencyData(bins);
			setBands(bandsFromFrequency(bins, barCount));
			frame = requestAnimationFrame(draw);
		};
		frame = requestAnimationFrame(draw);
		return () => {
			cancelAnimationFrame(frame);
			analyser.cleanup();
		};
	}, [active, barCount, stream]);

	return stream ? bands : animated;
}

function attachStreamAnalyser(stream: MediaStream): {
	frequencyBinCount: number;
	getByteFrequencyData: (bins: Uint8Array<ArrayBuffer>) => void;
	cleanup: () => void;
} | null {
	if (typeof window === "undefined") return null;
	const AudioCtx =
		window.AudioContext ??
		(window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
	if (!AudioCtx) return null;
	try {
		const context = new AudioCtx();
		const analyser = context.createAnalyser();
		analyser.fftSize = 64;
		analyser.smoothingTimeConstant = 0.65;
		const node = context.createMediaStreamSource(stream);
		node.connect(analyser);
		void context.resume();
		return {
			frequencyBinCount: analyser.frequencyBinCount,
			getByteFrequencyData: (bins) => analyser.getByteFrequencyData(bins),
			cleanup: () => {
				node.disconnect();
				void context.close();
			},
		};
	} catch {
		return null;
	}
}

function bandsFromFrequency(bins: Uint8Array, barCount: number): number[] {
	const start = Math.floor(bins.length * 0.08);
	const end = Math.max(start + 1, Math.floor(bins.length * 0.7));
	const slice = end - start;
	const chunk = Math.max(1, Math.ceil(slice / barCount));
	return Array.from({ length: barCount }, (_, index) => {
		let sum = 0;
		let count = 0;
		const from = start + index * chunk;
		const to = Math.min(end, from + chunk);
		for (let i = from; i < to; i += 1) {
			sum += bins[i] ?? 0;
			count += 1;
		}
		return Math.min(1, Math.max(0.12, count ? sum / count / 255 : 0.12));
	});
}

function demoBands(barCount: number, time: number): number[] {
	return Array.from({ length: barCount }, (_, index) => {
		const wave = Math.sin(time * 2.4 + index * 0.55) * 0.28 + 0.46;
		const noise = ((index * 17 + Math.floor(time * 8)) % 9) / 40;
		return Math.min(1, Math.max(0.14, wave + noise));
	});
}

function snapshotBands(seed: string, barCount: number): number[] {
	let n = 2166136261;
	for (let i = 0; i < seed.length; i += 1) n = Math.imul(n ^ seed.charCodeAt(i), 16777619);
	return Array.from({ length: barCount }, (_, index) => {
		n = Math.imul(n ^ (index + 1), 16777619) >>> 0;
		const swell = 0.28 + 0.22 * Math.sin(index * 0.62);
		return Math.min(0.82, Math.max(0.16, swell + (n % 37) / 120));
	});
}

function padBands(bands: number[], barCount: number): number[] {
	if (bands.length >= barCount) return bands.slice(0, barCount);
	return [...bands, ...Array.from({ length: barCount - bands.length }, () => 0.2)];
}

function formatClock(seconds: number): string {
	const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
	const whole = Math.floor(safe);
	const minutes = Math.floor(whole / 60);
	return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

function pickRecorderMime(): string {
	const types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg"];
	if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return "";
	return types.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function stopTracks(stream: MediaStream | null): void {
	if (!stream) return;
	for (const track of stream.getTracks()) track.stop();
}

function microphoneErrorMessage(error: unknown): string {
	if (error instanceof DOMException) {
		if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
			return "No microphone was found on this device.";
		}
		if (error.name === "NotReadableError" || error.name === "TrackStartError") {
			return "The microphone is in use by another app. Close it and try again.";
		}
		if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
			return "The microphone is blocked. Allow it in the browser to send a voice message.";
		}
		if (error.name === "SecurityError") {
			return "This page cannot use the microphone. Refresh the page and try again.";
		}
	}
	return "The microphone is blocked. Allow it in the browser to send a voice message.";
}
